import { useAuthReady } from './useAuthReady';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchStaking,
  openPosition,
  syncStakeTaps,
  withdrawFromPosition,
  type AccrualPeriod,
  type LegacyPlan,
  type StakePosition,
  type StakingOverview,
  type Vault,
  type VaultKind,
  type WithdrawDestination,
  type WithdrawMode,
} from '../lib/staking';
import { stakingLegalForLocale } from '../lib/stakingLegal';
import { mockStaking, summarize } from '../lib/staking.mock';

/**
 * Data layer for the stake screen. `/v1/staking` is not built yet, so set
 * `VITE_STAKING_MOCK=1` to serve the prototype fixture instead — the component
 * tree sees the same shapes either way, and flipping the flag off is the only
 * change needed once the backend lands.
 */
export const STAKING_MOCK = import.meta.env.VITE_STAKING_MOCK === '1';

/** Positions accrue on a timer, so refresh while the screen is open. */
export const STAKING_POLL_MS = 30_000;

const STAKING_KEY = ['staking'] as const;

/** Latency so mocked writes exercise the same pending states as real ones. */
const MOCK_LATENCY_MS = 450;
const settle = () => new Promise((r) => setTimeout(r, MOCK_LATENCY_MS));

export function useStaking() {
  const { canFetch, initData } = useAuthReady();
  const { i18n } = useTranslation();
  const ready = STAKING_MOCK || canFetch;

  const q = useQuery({
    queryKey: STAKING_KEY,
    enabled: ready,
    refetchOnWindowFocus: true,
    refetchInterval: STAKING_POLL_MS,
    queryFn: () => (STAKING_MOCK ? Promise.resolve(mockStaking()) : fetchStaking(initData)),
  });

  const data = q.data;
  const vaults = data?.vaults ?? [];
  const legal = stakingLegalForLocale(i18n.language);
  const treasuryTrx = data?.treasuryTrx ?? null;
  const legacyPlans = data?.legacyPlans ?? [];
  const positions = data?.positions ?? [];

  /** The vault a position runs on, or undefined for a legacy position. */
  const vaultOf = (p: StakePosition) => vaults.find((v) => v.kind === p.vaultKind);
  /** The discontinued plan a position runs on, or undefined for a current one. */
  const planOf = (p: StakePosition) => legacyPlans.find((l) => l.id === p.legacyPlanId);

  /** Withdraw rules and payout cadence, whichever line the position is on. */
  const termsOf = (p: StakePosition): { withdrawMode: WithdrawMode; accrual: AccrualPeriod } => {
    const v = vaultOf(p);
    if (v) return { withdrawMode: v.withdrawMode, accrual: v.accrual };
    const l = planOf(p);
    return { withdrawMode: l?.withdrawMode ?? 'full', accrual: l?.accrual ?? 'daily' };
  };

  // Default false until the overview lands — vaults stay hidden while the
  // product gate is still unknown (gate off = no new deposits, tab stays).
  const visible = data?.visible ?? false;

  return {
    visible,
    vaults,
    legal,
    treasuryTrx,
    fees: data?.fees ?? { walletUsd: 0, externalUsd: 1 },
    accrualEveryHours: data?.accrualEveryHours ?? 24,
    legacyPlans,
    positions,
    summary: data?.summary ?? {
      total: 0,
      invested: 0,
      profit: 0,
      byVault: emptyByVault(),
      legacy: 0,
    },
    tap: data?.tap ?? { likes: 0, likeBonus: 0, goal: 100, rewardUsd: 1, lastSeq: 0 },
    vaultOf,
    planOf,
    termsOf,
    loading: q.isLoading,
    error: q.isError,
  };
}

const emptyByVault = (): Record<VaultKind, number> => ({ liquid: 0, monthly: 0, maximum: 0 });

/* ── Mutations ────────────────────────────────────────────────────────────── */

/** Rewrite the cached overview, keeping the summary consistent with positions. */
function patchCache(qc: QueryClient, edit: (o: StakingOverview) => StakePosition[]) {
  qc.setQueryData<StakingOverview>(STAKING_KEY, (prev) => {
    if (!prev) return prev;
    const positions = edit(prev);
    return { ...prev, positions, summary: summarize(positions) };
  });
}

const recompute = (p: StakePosition, mode: WithdrawMode): StakePosition => {
  const current = p.invested + p.profit - p.withdrawn;
  return {
    ...p,
    current,
    available:
      p.lockEndsAt && new Date(p.lockEndsAt).getTime() > Date.now()
        ? 0
        : mode === 'profit'
          ? p.profit - p.withdrawn
          : current,
  };
};

/**
 * Open / top up / withdraw. Under the mock flag the same calls edit the cached
 * fixture so the flows can be walked end to end without a backend.
 */
export function useStakingActions() {
  const { initData } = useAuthReady();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: STAKING_KEY });
    // A stake moves money out of the wallet balance (and back on withdrawal).
    void qc.invalidateQueries({ queryKey: ['balances'] });
  };

  const open = useMutation({
    mutationFn: async ({
      vault,
      amount,
      paymentTransferId,
    }: {
      vault: Vault;
      amount: number;
      paymentTransferId?: string;
    }) => {
      if (!STAKING_MOCK) {
        if (!paymentTransferId) throw new Error('payment_not_found');
        await openPosition(
          initData,
          { vault_kind: vault.kind, payment_transfer_id: paymentTransferId },
          crypto.randomUUID(),
        );
        return;
      }
      await settle();
      patchCache(qc, (o) => [mockOpen(o, vault, amount), ...o.positions]);
    },
    onSuccess: () => {
      if (!STAKING_MOCK) invalidate();
    },
  });

  const withdraw = useMutation({
    mutationFn: async ({
      position,
      amount,
      destination,
      mode,
      reauthToken,
    }: {
      position: StakePosition;
      amount: number;
      destination: WithdrawDestination;
      mode: WithdrawMode;
      reauthToken?: string;
    }) => {
      if (!STAKING_MOCK) {
        return withdrawFromPosition(
          initData,
          position.id,
          { withdraw_all: true, destination },
          crypto.randomUUID(),
          reauthToken,
        );
      }
      await settle();
      patchCache(qc, (o) =>
        o.positions.map((p) =>
          p.id === position.id ? recompute({ ...p, withdrawn: p.withdrawn + amount }, mode) : p,
        ),
      );
    },
    onSuccess: () => {
      if (!STAKING_MOCK) invalidate();
    },
  });

  const tap = useMutation({
    mutationFn: async ({ seq, count }: { seq: number; count: number }) => {
      if (!STAKING_MOCK) return syncStakeTaps(initData, { seq, count });

      await settle();
      let result: Awaited<ReturnType<typeof syncStakeTaps>> | undefined;
      qc.setQueryData<StakingOverview>(STAKING_KEY, (prev) => {
        if (!prev) return prev;
        const liquid = prev.positions.find(
          (position) => position.vaultKind === 'liquid' && position.status === 'active',
        );

        let likes = prev.tap.likes;
        let likeBonus = prev.tap.likeBonus;
        let goal = prev.tap.goal;
        let remaining = count;
        let rewards = 0;
        let invested = liquid?.invested ?? 0;
        while (remaining > 0) {
          const need = goal - likes;
          if (remaining < need) {
            likes += remaining;
            remaining = 0;
            break;
          }
          remaining -= need;
          likes = 0;
          likeBonus += 1;
          goal *= 2;
          invested += 1;
          rewards += 1;
        }
        const tapState = {
          likes,
          likeBonus,
          goal,
          rewardUsd: 1,
          lastSeq: seq,
        };
        const vault = prev.vaults.find((item) => item.kind === 'liquid');
        if (rewards > 0 && !liquid && !vault) throw new Error('liquid_required');
        const position =
          rewards > 0 && liquid
            ? recompute({ ...liquid, invested }, 'full')
            : rewards > 0 && vault
              ? mockOpen(prev, vault, invested)
              : undefined;
        const positions = position
          ? liquid
            ? prev.positions.map((item) => (item.id === position.id ? position : item))
            : [...prev.positions, position]
          : prev.positions;
        result = {
          tap: { ...tapState, claimed: count, accepted: count, rewards },
          position,
        };
        return { ...prev, tap: tapState, positions, summary: summarize(positions) };
      });
      if (!result) throw new Error('staking_unavailable');
      return result;
    },
    onSuccess: (result, variables) => {
      if (STAKING_MOCK) return;
      qc.setQueryData<StakingOverview>(STAKING_KEY, (prev) => {
        if (!prev) return prev;
        const tapState = {
          likes: result.tap.likes,
          likeBonus: result.tap.likeBonus,
          goal: result.tap.goal,
          rewardUsd: result.tap.rewardUsd,
          lastSeq: result.tap.lastSeq ?? variables.seq,
        };
        const positions = result.position
          ? prev.positions.some((item) => item.id === result.position!.id)
            ? prev.positions.map((item) =>
                item.id === result.position!.id ? result.position! : item,
              )
            : [...prev.positions, result.position]
          : prev.positions;
        return {
          ...prev,
          tap: tapState,
          positions,
          summary: summarize(positions),
        };
      });
      if (result.tap.rewards > 0) {
        void qc.invalidateQueries({ queryKey: STAKING_KEY });
      }
    },
  });

  return { open, withdraw, tap };
}

/** A freshly opened position, as the backend would return it. */
function mockOpen(o: StakingOverview, vault: Vault, amount: number): StakePosition {
  const now = Date.now();
  // Profit accrues daily on every vault; payout cadence is separate.
  const nextId = o.positions.reduce((max, p) => Math.max(max, p.id), 0) + 1;
  return {
    id: nextId,
    vaultKind: vault.kind,
    legacyPlanId: null,
    invested: amount,
    profit: 0,
    withdrawn: 0,
    current: amount,
    available: vault.withdrawMode === 'full' ? amount : 0,
    openedAt: new Date(now).toISOString(),
    nextAccrualAt: new Date(now + 86_400_000).toISOString(),
    // Monthly + Maximum commit the principal for the term (same as hasTerm).
    lockEndsAt:
      vault.withdrawMode !== 'full'
        ? new Date(now + vault.termDays * 86_400_000).toISOString()
        : null,
    status: 'active',
  };
}

export type { LegacyPlan, StakePosition, Vault, VaultKind };
